/** @format */

import React, { FunctionComponent, ReactElement, useCallback, useEffect, useState } from 'react';
import { makeStyles, useTheme, withStyles } from '@material-ui/core/styles';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import { useDispatch, useSelector } from 'react-redux';
import { TableBody, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableCell from '@material-ui/core/TableCell';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import FilterList from '../../components/Filter/FilterList';
import I18n from '../../components/Translate/I18n';
import PaperedTable from '../../components/Table/PaperedTable';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import { FilterReducerFieldName, FilterReducers } from '../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { ServicesMvneType } from '../../modules/lib/hooks/useOperatorType';
import { FilterManager } from '../../modules/filter/FilterManager';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { provConstant, ProvReducerType } from '../../modules/provisioning/ProvisioningConnect';
import { MnoMessageCollectionInterface, MnoMessageInterface } from '../../interfaces/collections/MnoMessageInterface';
import Pager from '../../components/Adescom/PagerRange';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import MnoMessageItem from './MnoMessageItem';
import { MnoMessageView } from './MnoMessageView';
import SimCardInterface from '../../interfaces/platform/SimCardInterface';
import { FeatureTypes } from '../../modules/features/FeaturesTypes';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import OplTheme from '../../assets/themes/OPL/OplTheme';

const _ = require('underscore');

type MnoMessageListType = {
    classes: any;
    showMainLoader(show: boolean): void;
    simCard?: SimCardInterface;
};

const MnoMessageList: FunctionComponent<MnoMessageListType> = ({ classes, showMainLoader, simCard }): ReactElement => {
    const setDefaultFilters = (): Array<Filter> => [{ name: 'notifyType[]', value: ServicesMvneType.SDG }];
    const setDefaultOrders = (): Array<Order> => [{ name: 'createdAt', value: 'desc' }];

    const dispatch = useDispatch();
    const [viewMnoMessage, setViewMnoMessage] = useState<MnoMessageInterface | null>(null);
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const { mnoMessages } = useSelector((state: { provReducer: ProvReducerType }) => state.provReducer);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [activeRow, setActiveRow] = useState(-1);
    const [reload, setReload] = useState(false);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);

    const listType = new FeatureTypes(process.env);

    const processFilters = useCallback(
        (filterProcess: Array<Filter>): Array<Filter> => {
            const newFilters: Array<Filter> = [];
            for (let i = 0; i < filterProcess.length; i++) {
                let value = filterProcess[i].value;

                if (filterProcess[i].name === 'notifyType[]') {
                    continue;
                }

                if (Array.isArray(value)) {
                    for (let j = 0; j < value.length; j++) {
                        let valueT = value[j];
                        if (filterProcess[i].name === 'serviceType') {
                            if (typeof valueT === 'number') {
                                newFilters.push({ name: filterProcess[i].name + '[]', value: listType.mapToString(valueT) });
                            }
                        } else {
                            newFilters.push({ name: filterProcess[i].name + '[]', value: valueT });
                        }
                    }
                } else {
                    newFilters.push(filterProcess[i]);
                }
            }
            for (let i = 0; i < listType.operatorType.length; i++) {
                newFilters.push({ name: 'operatorType[]', value: listType.operatorType[i] });
            }
            if (simCard) {
                newFilters.push({ name: 'simCard.id', value: simCard.id });
            }
            return newFilters;
        },
        [listType, simCard]
    );

    useEffect(() => {
        if (reload) {
            setReload(false);
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mno_messages')),
                provConstant.MNO_MESSAGE_GET,
                processFilters(filters),
                orders,
                (data: MnoMessageCollectionInterface) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    dispatch({
                        type: provConstant.MNO_MESSAGE_GET,
                        mnoMessages: data,
                    } as ProvReducerType);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, filters, orders, reload, showMainLoader, processFilters]);

    useEffect(() => {
        setReload(true);
    }, [orders]);

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            provConstant.MNO_MESSAGE_GET,
            (data: MnoMessageCollectionInterface): void => {
                dispatch({
                    type: provConstant.MNO_MESSAGE_GET,
                    mnoMessages: data,
                } as ProvReducerType);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const viewCallback = (mnoMessage: MnoMessageInterface): void => setViewMnoMessage(mnoMessage);

    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={(): void => {
                                        showMainLoader(true);
                                        setReload(true);
                                    }}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {showClearFilterButton && (
                                    <CustomTextButtom
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                            </GridItem>
                            <FilterList />
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            {!simCard && (
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="simcard:list.msisdn"
                                        fieldName="msisdn"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="number"
                                        prependMaxLengthRequirement={9}
                                        filters={filters}
                                        sort={orders}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        searchActionCallback={firstLoad}
                                    />
                                </TableCell>
                            )}
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:features.serviceType"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="notifyType"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={[
                                        { name: 'SDG', value: 2 },
                                        { name: 'Welcome SMS', value: 3 },
                                        { name: 'USSD', value: 4 },
                                        { name: 'Heavy User', value: 5 },
                                        { name: 'Api Gee', value: 6 },
                                    ]}
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:messages.processed"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="processed"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:messages.exception"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="exception"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:messages.xml_parse_error"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="xmlParseError"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:messages.created_at"
                                    fieldName="createdAt"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:messages.processed_time"
                                    fieldName="processedTime"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={true}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mnoMessages &&
                            mnoMessages['hydra:member'].map((listItem) => {
                                return (
                                    <MnoMessageItem
                                        key={'mnoMessageId' + listItem.id}
                                        mnoMessage={listItem}
                                        activeRow={activeRow}
                                        setActiveRow={setActiveRow}
                                        viewCallback={viewCallback}
                                        simCard={simCard}
                                    />
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {mnoMessages && mnoMessages['hydra:view'] && <Pager callback={paginationCallbackFunction} views={mnoMessages} />}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
                <MnoMessageView
                    openView={!!viewMnoMessage}
                    fullWidth={true}
                    title={null}
                    closeCallback={(): void => {
                        setViewMnoMessage(null);
                    }}
                    mnoMessage={viewMnoMessage}
                />
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(MnoMessageList);
